// Pure matcher for the NMJL 2026 card. Validates winning hands, scores how well
// a hand fits a card hand (for AI targeting + hints), and exposes the needed
// tiles of the best fit. No DOM, no globals, no randomness — safe for Deno.
//
// Joker rule (NMJL): a joker may fill any tile in a group of 3+ (pung, kong,
// quint, sextet) and NEVER a single or a pair. Every option lists groups whose
// `n` encodes this — `n >= 3` ⇒ jokers allowed.

import { tileKey } from './tiles.js';
import { HANDS } from './cards/nmjl2026.js';

const SUITS3 = ['crak', 'bam', 'dot'];
const DRAGON_OF_SUIT = { crak: 'red', bam: 'green', dot: 'white' };
const WIND_FULL = { N: 'north', E: 'east', W: 'west', S: 'south' };

// ── instantiation ───────────────────────────────────────────────────────────
function parseNum(expr, vars) {
  if (typeof expr === 'number') return expr;
  const m = /^([a-z]\w*)(?:\+(\d+))?$/.exec(expr);
  if (!m) return null;
  const base = vars[m[1]];
  if (base == null) return null;
  return base + (m[2] ? Number(m[2]) : 0);
}

function groupKey(g, vars, suitMap) {
  if (g.flower) return 'flower';
  if (g.wind != null) {
    const letter = WIND_FULL[g.wind] ? g.wind : vars[g.wind];
    return 'wind_' + WIND_FULL[letter];
  }
  if (g.dragon != null) {
    let d = g.dragon;
    if (d.indexOf('match:') === 0) d = DRAGON_OF_SUIT[suitMap[d.slice(6)]];
    else if (d !== 'red' && d !== 'green' && d !== 'white') d = vars[d];
    return 'dragon_' + d;
  }
  const num = parseNum(g.num, vars);
  if (num === 0) return 'dragon_white'; // White Dragon as zero
  return suitMap[g.suit] + num;
}

function varDomain(def) {
  if (def.set) return def.set;
  if (def.winds) return ['N', 'E', 'W', 'S'];
  if (def.dragons) return ['red', 'green', 'white'];
  return [];
}

function* enumVars(names, vars, i, acc) {
  if (i === names.length) { yield acc; return; }
  const name = names[i];
  const def = vars[name];
  for (const v of varDomain(def)) {
    if (def.ne && def.ne.some((o) => acc[o] === v)) continue;
    acc[name] = v;
    yield* enumVars(names, vars, i + 1, acc);
  }
  delete acc[name];
}

function suitLetters(groups) {
  const s = new Set();
  for (const g of groups) {
    if (g.suit && !(typeof g.num === 'number' && g.num === 0)) s.add(g.suit);
    if (typeof g.dragon === 'string' && g.dragon.indexOf('match:') === 0) s.add(g.dragon.slice(6));
  }
  return [...s];
}

function* enumSuitMaps(letters, i, used, acc) {
  if (i === letters.length) { yield acc; return; }
  for (const suit of SUITS3) {
    if (used.has(suit)) continue;
    used.add(suit); acc[letters[i]] = suit;
    yield* enumSuitMaps(letters, i + 1, used, acc);
    used.delete(suit);
  }
}

// Yields concrete group lists [{ key, n, joker }] for an option.
function* optionInstantiations(option) {
  const varNames = option.vars ? Object.keys(option.vars) : [];
  const letters = suitLetters(option.groups);
  const vIter = varNames.length ? enumVars(varNames, option.vars, 0, {}) : [{}];
  for (const vAssign of vIter) {
    const sIter = letters.length ? enumSuitMaps(letters, 0, new Set(), {}) : [{}];
    for (const sMap of sIter) {
      yield option.groups.map((g) => ({ key: groupKey(g, vAssign, sMap), n: g.n, joker: g.n >= 3 }));
    }
  }
}

// ── matching ────────────────────────────────────────────────────────────────
function countTiles(tiles) {
  const have = new Map();
  let jokers = 0;
  for (const t of tiles) {
    if (t.type === 'joker') { jokers++; continue; }
    const k = tileKey(t);
    have.set(k, (have.get(k) || 0) + 1);
  }
  return { have, jokers };
}

// Exact-cover test: do the player's tiles form exactly this concrete hand?
function matchConcrete(base, concrete) {
  const have = new Map(base.have);
  let jokers = base.jokers;
  // Singles/pairs first so they claim real tiles before joker-eligible groups.
  const ordered = concrete.slice().sort((a, b) => (a.joker ? 1 : 0) - (b.joker ? 1 : 0));
  for (const g of ordered) {
    const avail = have.get(g.key) || 0;
    if (!g.joker) {
      if (avail < g.n) return false;
      have.set(g.key, avail - g.n);
    } else {
      const useReal = Math.min(avail, g.n);
      have.set(g.key, avail - useReal);
      jokers -= g.n - useReal;
      if (jokers < 0) return false;
    }
  }
  if (jokers !== 0) return false;                       // every joker must be used
  for (const v of have.values()) if (v !== 0) return false; // no leftover real tiles
  return true;
}

/**
 * Does this set of 14 tiles complete a real 2026 hand?
 * @param allTiles flat array of the player's 14 tiles (concealed hand + every exposed tile)
 * @param hasExposures whether the player has made any exposure (blocks concealed hands)
 * @param onlyHand optional single hand to test (else all hands)
 * @returns { hand, value } or null
 */
export function validateWin(allTiles, hasExposures, onlyHand) {
  if (allTiles.length !== 14) return null;
  const base = countTiles(allTiles);
  const hands = onlyHand ? [onlyHand] : HANDS;
  for (const hand of hands) {
    if (hand.concealed && hasExposures) continue;
    for (const option of hand.options) {
      for (const concrete of optionInstantiations(option)) {
        if (matchConcrete(base, concrete)) return { hand, value: hand.value };
      }
    }
  }
  return null;
}

// ── fit (AI targeting + hints) ───────────────────────────────────────────────
// How close are these tiles to `hand`? Returns the best instantiation's needed
// tiles plus a score = matched real tiles + usable jokers. Higher = closer.
export function handFit(tiles, hand, hasExposures = false) {
  if (hand.concealed && hasExposures) return { hand, score: -1, need: new Map() };
  const base = countTiles(tiles);
  let best = { hand, score: -1, need: new Map() };
  for (const option of hand.options) {
    for (const concrete of optionInstantiations(option)) {
      const need = new Map();
      let jokerSlots = 0;
      for (const g of concrete) {
        need.set(g.key, (need.get(g.key) || 0) + g.n);
        if (g.joker) jokerSlots += g.n;
      }
      let real = 0;
      for (const [k, n] of need) real += Math.min(base.have.get(k) || 0, n);
      const score = real + Math.min(base.jokers, jokerSlots);
      if (score > best.score) best = { hand, score, need };
    }
  }
  return best;
}

// Every hand ranked by fit (best first).
export function bestHands(tiles, hasExposures = false) {
  return HANDS.map((hand) => handFit(tiles, hand, hasExposures)).sort((a, b) => b.score - a.score);
}

// Does this tile help the target hand? `need` is the best fit's needed-tile map.
export function tileFitsTarget(tile, need) {
  if (tile.type === 'joker') return need.size > 0;
  return (need.get(tileKey(tile)) || 0) > 0;
}

// Test helper: the first concrete group list [{ key, n, joker }] of an option.
export function firstConcrete(hand, optIndex = 0) {
  for (const c of optionInstantiations(hand.options[optIndex])) return c;
  return null;
}

export { HANDS };
